import * as React from 'react';
import { FieldArray, FieldArrayRenderProps } from 'formik';

import { GeneralInformationFormData, ContactInfo } from './types';
import { SpinnerOverlay } from '@weddingspot/ws-core-components';
import { WsFormikError } from '../../utils/formik/error';
import ConfirmModal from '../modal-confirm';
import { hbToastSuccess, hbToastError } from '../toast';

interface OwnProps {
    disabled: boolean;
    values: GeneralInformationFormData;

    // Handlers
    showContactsModal: (idx?: number) => void;
    searchEmail: (email: string) => Promise<ContactInfo | null>;
    deleteContact: (id: number) => Promise<void>;
    sendAccountInvite: (contactInfo: ContactInfo) => Promise<void>;
}

interface State {
    deletingContactIdx: number | null;
    sendingAccountInviteIdx: number | null;

    confirmDeleteModalShowing: boolean;
    deleteHandler: () => Promise<void>;
}

export class ContactsCard extends React.Component<OwnProps, State> {
    constructor(props: OwnProps) {
        super(props);

        // default state
        this.state = {
            deletingContactIdx: null,
            sendingAccountInviteIdx: null,
            confirmDeleteModalShowing: false,
            deleteHandler: () => Promise.resolve(),
        };

        this.handleDelete = this.handleDelete.bind(this);
        this.setDeletingContact = this.setDeletingContact.bind(this);
        this.doneDeletingContact = this.doneDeletingContact.bind(this);
        this.setSendingInvite = this.setSendingInvite.bind(this);
        this.setDoneSendingInvite = this.setDoneSendingInvite.bind(this);
        this.handleSendAccountInvite = this.handleSendAccountInvite.bind(this);
        this.showConfirmDeleteModal = this.showConfirmDeleteModal.bind(this);
        this.hideConfirmDeleteModal = this.hideConfirmDeleteModal.bind(this);
    }

    render() {
        const { values } = this.props;

        return (
            <div className='VenueManagement--card VenueManagement--contacts'>
                {this.props.disabled && (
                    <div className='VenueManagement--card-overlay'>
                        <div className='VenueManagement--card-overlay--content'>You must save the venue first before editing contacts.</div>
                    </div>
                )}
                <div className='VenueManagement--card--header'>Contacts</div>
                <div className='VenueManagement--card--body'>
                    <FieldArray
                        name='contacts'
                        render={(arrayHelpers: FieldArrayRenderProps) => {
                            return (
                                <div>
                                    {!!arrayHelpers.form.errors.contacts && (
                                        <div className='VenueManagement--form-level-error'>{arrayHelpers.form.errors.contacts}</div>
                                    )}
                                    <div className='VenueManagement--row'>
                                        <div className='VenueManagement--col VenueManagement--contacts--info-col'>
                                            <div
                                                className={`VenueManagement--contacts--cell
                                                        VenueManagement--contacts--cell__header`}
                                            >
                                                Contact Info
                                            </div>
                                            {values.contacts.map((item, idx) => (
                                                <div key={idx} className='VenueManagement--contacts--cell'>
                                                    <div>
                                                        {item.first_name} {item.last_name}
                                                    </div>
                                                    <div>
                                                        <a href='javascript:void(0);' onClick={() => this.showContactsModal(idx)}>
                                                            {item.email}
                                                        </a>
                                                    </div>
                                                    <div>{item.phone_number}</div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className='VenueManagement--col'>
                                            <div
                                                className={`VenueManagement--contacts--cell
                                                        VenueManagement--contacts--cell__header`}
                                            >
                                                Account Created
                                            </div>
                                            {values.contacts.map((item, idx) => (
                                                <div key={idx} className='VenueManagement--contacts--cell'>
                                                    <span className={item.account_created ? 'icon-hb-nx-check' : 'icon-hb-nx-close'} />
                                                </div>
                                            ))}
                                        </div>
                                        <div className='VenueManagement--col'>
                                            <div
                                                className={`VenueManagement--contacts--cell
                                                        VenueManagement--contacts--cell__header`}
                                            >
                                                Receive Appointments
                                            </div>
                                            {values.contacts.map((item, idx) => (
                                                <div key={idx} className='VenueManagement--contacts--cell'>
                                                    <span className={item.receive_appointments ? 'icon-hb-nx-check' : 'icon-hb-nx-close'} />
                                                </div>
                                            ))}
                                        </div>
                                        <div className='VenueManagement--col'>
                                            <div
                                                className={`VenueManagement--contacts--cell
                                                        VenueManagement--contacts--cell__header`}
                                            >
                                                Receive invoices
                                            </div>
                                            {values.contacts.map((item, idx) => (
                                                <div key={idx} className='VenueManagement--contacts--cell'>
                                                    <span className={item.receive_invoices ? 'icon-hb-nx-check' : 'icon-hb-nx-close'} />
                                                </div>
                                            ))}
                                        </div>
                                        <div className='VenueManagement--col'>
                                            <div
                                                className={`VenueManagement--contacts--cell
                                                        VenueManagement--contacts--cell__header`}
                                            >
                                                Primary user
                                            </div>
                                            {values.contacts.map((item, idx) => (
                                                <div key={idx} className='VenueManagement--contacts--cell'>
                                                    <span className={item.is_primary ? 'icon-hb-nx-check' : 'icon-hb-nx-close'} />
                                                </div>
                                            ))}
                                        </div>
                                        <div className='VenueManagement--col'>
                                            <div
                                                className={`VenueManagement--contacts--cell
                                                        VenueManagement--contacts--cell__header`}
                                            >
                                                Receive approval requests
                                            </div>
                                            {values.contacts.map((item, idx) => (
                                                <div key={idx} className='VenueManagement--contacts--cell'>
                                                    <span
                                                        className={item.receive_approval_requests ? 'icon-hb-nx-check' : 'icon-hb-nx-close'}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <div className='VenueManagement--col'>
                                            <div
                                                className={`VenueManagement--contacts--cell
                                                        VenueManagement--contacts--cell__header`}
                                            >
                                                Receive metrics emails
                                            </div>
                                            {values.contacts.map((item, idx) => (
                                                <div key={idx} className='VenueManagement--contacts--cell'>
                                                    <span
                                                        className={item.receive_metrics_emails ? 'icon-hb-nx-check' : 'icon-hb-nx-close'}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <div className='VenueManagement--col'>
                                            <div
                                                className={`VenueManagement--contacts--cell
                                                        VenueManagement--contacts--cell__header`}
                                            >
                                                Access venue metrics
                                            </div>
                                            {values.contacts.map((item, idx) => (
                                                <div key={idx} className='VenueManagement--contacts--cell'>
                                                    <span className={item.access_venue_metrics ? 'icon-hb-nx-check' : 'icon-hb-nx-close'} />
                                                </div>
                                            ))}
                                        </div>
                                        <div className='VenueManagement--col'>
                                            <div
                                                className={`VenueManagement--contacts--cell
                                                        VenueManagement--contacts--cell__header`}
                                            >
                                                Invite
                                            </div>
                                            {values.contacts.map((item, idx) => (
                                                <div key={idx} className='VenueManagement--contacts--cell'>
                                                    <SpinnerOverlay
                                                        isSpinning={this.state.sendingAccountInviteIdx === idx}
                                                        spinnerProps={{
                                                            spinnerColor: 'black',
                                                            spinnerDiameter: 25,
                                                        }}
                                                    >
                                                        <span
                                                            className={`icon-hb-nx-email
                                                                    VenueManagement--contacts--action-btn`}
                                                            onClick={() => this.handleSendAccountInvite(item, idx, arrayHelpers)}
                                                        />
                                                    </SpinnerOverlay>
                                                </div>
                                            ))}
                                        </div>
                                        <div className='VenueManagement--col'>
                                            <div
                                                className={`VenueManagement--contacts--cell
                                                        VenueManagement--contacts--cell__header`}
                                            >
                                                Edit
                                            </div>
                                            {values.contacts.map((item, idx) => (
                                                <div key={idx} className='VenueManagement--contacts--cell'>
                                                    <span
                                                        className={`icon-hb-nx-pencil
                                                                VenueManagement--contacts--action-btn`}
                                                        onClick={() => this.showContactsModal(idx)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        <div className='VenueManagement--col'>
                                            <div
                                                className={`VenueManagement--contacts--cell
                                                        VenueManagement--contacts--cell__header`}
                                            >
                                                Delete
                                            </div>
                                            {values.contacts.map((item, idx) => (
                                                <div key={idx} className='VenueManagement--contacts--cell'>
                                                    <SpinnerOverlay
                                                        isSpinning={this.state.deletingContactIdx === idx}
                                                        spinnerProps={{
                                                            spinnerColor: 'black',
                                                            spinnerDiameter: 25,
                                                        }}
                                                    >
                                                        <span
                                                            className={`icon-hb-nx-trash
                                                                    VenueManagement--contacts--action-btn`}
                                                            onClick={() => this.showConfirmDeleteModal(item.id, idx, arrayHelpers)}
                                                        />
                                                    </SpinnerOverlay>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className='VenueManagement--brand-link' onClick={() => this.showContactsModal()}>
                                        + Add a contact
                                    </div>
                                </div>
                            );
                        }}
                    />
                </div>
                {this.state.confirmDeleteModalShowing && (
                    <ConfirmModal
                        header='Confirm delete'
                        content='Are you sure you want to delete this contact?'
                        primaryCta='Yes'
                        secondaryCta='No'
                        primaryAsync={true}
                        onPrimaryCtaClick={this.state.deleteHandler}
                        secondaryAsync={false}
                        onSecondaryCtaClick={() => this.hideConfirmDeleteModal()}
                    />
                )}
            </div>
        );
    }

    private handleDelete(id: number | null, index: number, arrayHelpers: FieldArrayRenderProps) {
        this.setDeletingContact(index);
        arrayHelpers.form.setFieldError('contacts', '');

        return new Promise<void>((resolve, reject) => {
            if (id === null) {
                arrayHelpers.remove(index);
                this.doneDeletingContact();
                resolve();
            } else {
                this.props
                    .deleteContact(id)
                    .catch((err: WsFormikError<{}>) => {
                        if (err.__errors__) {
                            arrayHelpers.form.setFieldError('contacts', err.__errors__);
                        } else {
                            arrayHelpers.form.setFieldError('contacts', 'Unknown error occurred, please try again later.');
                        }
                    })
                    .then(() => {
                        this.doneDeletingContact();
                    })
                    .then(() => resolve());
            }
        });
    }

    private showContactsModal(idx?: number) {
        this.props.showContactsModal(idx);
    }

    private showConfirmDeleteModal(id: number | null, index: number, arrayHelpers: FieldArrayRenderProps) {
        this.setState({
            confirmDeleteModalShowing: true,
            deleteHandler: () => {
                return this.handleDelete(id, index, arrayHelpers).then(this.hideConfirmDeleteModal);
            },
        });
    }

    private hideConfirmDeleteModal() {
        this.setState({
            confirmDeleteModalShowing: false,
            deleteHandler: () => Promise.resolve(),
        });
    }

    private setDeletingContact(idx: number) {
        this.setState({
            deletingContactIdx: idx,
        });
    }

    private doneDeletingContact() {
        this.setState({
            deletingContactIdx: null,
        });
    }

    private setSendingInvite(idx: number) {
        this.setState({
            sendingAccountInviteIdx: idx,
        });
    }

    private setDoneSendingInvite() {
        this.setState({
            sendingAccountInviteIdx: null,
        });
    }

    private handleSendAccountInvite(contactInfo: ContactInfo, idx: number, arrayHelpers: FieldArrayRenderProps) {
        if (contactInfo.id === null) {
            arrayHelpers.form.setFieldError('contacts', 'Could not sent invite, please refresh page and try again');
        } else {
            this.setSendingInvite(idx);
            arrayHelpers.form.setFieldError('contacts', '');

            this.props
                .sendAccountInvite(contactInfo)
                .then(() => {
                    hbToastSuccess('Successfully sent account invite');
                })
                .catch((err: WsFormikError<{}>) => {
                    if (err.__errors__) {
                        arrayHelpers.form.setFieldError('contacts', err.__errors__);
                        hbToastError('Could not send contacts. Please review error(s) and try again');
                    } else {
                        hbToastError('An unknown error occurred, please try again later');
                    }
                })
                .then(() => {
                    this.setDoneSendingInvite();
                });
        }
    }
}
